import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Modal,
  Form,
  Button,
} from "react-bootstrap";
import { Link, useHistory, useLocation } from "react-router-dom";
import HeaderTwo from "../../components/HeaderTwo";
import { BreadcrumbBox } from "../../components/common/Breadcrumb";
import FooterTwo from "../../components/FooterTwo";
import { Styles, AdminModalStyles } from "./styles/adminApplications.js";
import {
  ADMIN_APPLICATIONS_ENDPOINT,
  ADMIN_APPLICATIONS_UPDATE_ENDPOINT,
  ADMIN_APPLICATION_APPLICANT_ENDPOINT,
  INTAKES_ENDPOINT,
  ADMIN_AGENTS_ENDPOINT,
  STATUS_TYPES_ENDPOINT,
} from "../../config/api";
import { useAuth } from "../../context/AuthContext";

const DEFAULT_STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in_review", label: "In Review" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
];

const AdminApplications = () => {
  const history = useHistory();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editModal, setEditModal] = useState({
    show: false,
    application: null,
    status: "new",
    note: "",
    intakeId: "",
    agentId: "",
    saving: false,
    error: "",
  });
  const [profileModal, setProfileModal] = useState({
    show: false,
    profile: null,
    loading: false,
    error: "",
    email: "",
  });
  const [intakeFilter, setIntakeFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [intakeFilterQuery, setIntakeFilterQuery] = useState("");
  const [agentFilterQuery, setAgentFilterQuery] = useState("");
  const [intakes, setIntakes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loadingIntakes, setLoadingIntakes] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [intakeError, setIntakeError] = useState("");
  const [agentError, setAgentError] = useState("");
  const [statusTypes, setStatusTypes] = useState([]);
  const [loadingStatusTypes, setLoadingStatusTypes] = useState(false);
  const [statusTypesError, setStatusTypesError] = useState("");
  const [notesModal, setNotesModal] = useState({
    show: false,
    notes: [],
    title: "",
    meta: "",
  });

  const isAdmin = useMemo(() => {
    if (!Array.isArray(user?.roles)) {
      return false;
    }
    return user.roles.some(
      (role) => typeof role === "string" && role.toLowerCase() === "admin"
    );
  }, [user]);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        history.push(`${process.env.PUBLIC_URL || ""}/login`);
      }
    }
  }, [authLoading, user, history]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const nextIntake = params.get("intakeId") || "";
    const nextAgent = params.get("agentId") || "";
    const nextSearch = params.get("search") || "";

    setIntakeFilter((prev) => (prev === nextIntake ? prev : nextIntake));
    setAgentFilter((prev) => (prev === nextAgent ? prev : nextAgent));

    if (nextSearch !== searchTerm) {
      setSearchTerm(nextSearch);
    }
    if (searchInput !== nextSearch) {
      setSearchInput(nextSearch);
    }

    if (!nextIntake && intakeFilterQuery) {
      setIntakeFilterQuery("");
    }
    if (!nextAgent && agentFilterQuery) {
      setAgentFilterQuery("");
    }
  }, [
    location.search,
    intakeFilterQuery,
    agentFilterQuery,
    searchTerm,
    searchInput,
  ]);

  useEffect(() => {
    let isMounted = true;

    const fetchIntakes = async () => {
      setLoadingIntakes(true);
      setIntakeError("");
      try {
        const response = await fetch(INTAKES_ENDPOINT);
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.message || "Unable to load intakes.");
        }
        const payload = await response.json();
        if (isMounted) {
          setIntakes(Array.isArray(payload.intakes) ? payload.intakes : []);
        }
      } catch (err) {
        if (isMounted) {
          setIntakeError(err.message || "Unable to load intakes.");
          setIntakes([]);
        }
      } finally {
        if (isMounted) {
          setLoadingIntakes(false);
        }
      }
    };

    fetchIntakes();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchStatusTypes = async () => {
      setLoadingStatusTypes(true);
      setStatusTypesError("");
      try {
        const response = await fetch(STATUS_TYPES_ENDPOINT);
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.message || "Unable to load status types.");
        }
        const payload = await response.json();
        if (isMounted) {
          setStatusTypes(Array.isArray(payload.statuses) ? payload.statuses : []);
        }
      } catch (err) {
        if (isMounted) {
          setStatusTypesError(err.message || "Unable to load status types.");
          setStatusTypes([]);
        }
      } finally {
        if (isMounted) {
          setLoadingStatusTypes(false);
        }
      }
    };

    fetchStatusTypes();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (authLoading || !isAdmin) {
      return;
    }

    let isMounted = true;

    const fetchAgents = async () => {
      setLoadingAgents(true);
      setAgentError("");
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          throw new Error("Authentication required.");
        }
        const params = new URLSearchParams({ limit: "200", status: "active" });
        const response = await fetch(
          `${ADMIN_AGENTS_ENDPOINT}?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.message || "Unable to load agents.");
        }
        const payload = await response.json();
        if (isMounted) {
          setAgents(Array.isArray(payload.agents) ? payload.agents : []);
        }
      } catch (err) {
        if (isMounted) {
          setAgentError(err.message || "Unable to load agents.");
          setAgents([]);
        }
      } finally {
        if (isMounted) {
          setLoadingAgents(false);
        }
      }
    };

    fetchAgents();

    return () => {
      isMounted = false;
    };
  }, [authLoading, isAdmin]);

  useEffect(() => {
    let cancelled = false;

    const fetchApplications = async () => {
      if (!isAdmin || cancelled) {
        return;
      }

      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          throw new Error("Authentication required.");
        }

        const params = new URLSearchParams({
          page: String(page),
          limit: String(pageSize),
        });
        if (searchTerm) {
          params.set("search", searchTerm);
        }
        if (intakeFilter) {
          params.set("intakeId", intakeFilter);
        }
        if (agentFilter) {
          params.set("agentId", agentFilter);
        }

        const response = await fetch(
          `${ADMIN_APPLICATIONS_ENDPOINT}?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.message || "Unable to load applications.");
        }

        const payload = await response.json();
        if (cancelled) {
          return;
        }
        setApplications(payload.applications || []);
        if (payload.pagination) {
          const serverPage = payload.pagination.page || page;
          setPagination({
            page: serverPage,
            limit: payload.pagination.limit || pageSize,
            totalItems: payload.pagination.totalItems || 0,
            totalPages: payload.pagination.totalPages || 1,
          });
          if (serverPage !== page) {
            setPage(serverPage);
          }
        } else {
          setPagination({
            page,
            limit: pageSize,
            totalItems: payload.applications?.length || 0,
            totalPages: 1,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Unable to load applications.");
          setApplications([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (!authLoading && isAdmin) {
      fetchApplications();
    }

    return () => {
      cancelled = true;
    };
  }, [
    authLoading,
    isAdmin,
    page,
    pageSize,
    searchTerm,
    intakeFilter,
    agentFilter,
  ]);

  useEffect(() => {
    setPage((prev) => (prev === 1 ? prev : 1));
  }, [searchTerm, intakeFilter, agentFilter]);

  const customStatusOptions = useMemo(
    () =>
      statusTypes.map((status) => ({
        value: status.name,
        label: status.name,
      })),
    [statusTypes]
  );

  const allStatusOptions = useMemo(() => {
    const map = new Map();
    DEFAULT_STATUS_OPTIONS.forEach((opt) => {
      map.set(opt.value.toLowerCase(), {
        value: opt.value,
        label: opt.label,
      });
    });
    customStatusOptions.forEach((opt) => {
      const key = (opt.value || "").toLowerCase();
      if (!map.has(key)) {
        map.set(key, { value: opt.value, label: opt.label });
      }
    });
    return Array.from(map.values());
  }, [customStatusOptions]);

  const statusLabelMap = useMemo(() => {
    const map = new Map();
    allStatusOptions.forEach((opt) => {
      map.set(opt.value, opt.label);
      map.set((opt.value || "").toLowerCase(), opt.label);
    });
    return map;
  }, [allStatusOptions]);

  const tableRows = useMemo(
    () =>
      applications.map((application) => {
        const course = application.course || {};
        const courseId = course._id || application.course;
        const program =
          application.courseProgramName || course.programName || "N/A";
        const university =
          application.courseUniversityName || course.universityName || "N/A";
        const submittedOn = application.createdAt
          ? new Date(application.createdAt).toLocaleString()
          : "N/A";
        const applicantEmail = application.applicantEmail || "";

        const notes = Array.isArray(application.adminNotes)
          ? application.adminNotes.filter((entry) => entry && entry.note)
          : [];
        if (
          notes.length === 0 &&
          typeof application.adminNote === "string" &&
          application.adminNote.trim()
        ) {
          notes.push({
            note: application.adminNote.trim(),
            createdAt: application.updatedAt || application.createdAt || null,
          });
        }
        const sortedNotes = [...notes].sort((a, b) => {
          const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bTime - aTime;
        });
        const noteHistory = sortedNotes.map((entry) => ({
          note: entry.note,
          createdAt: entry.createdAt
            ? new Date(entry.createdAt).toLocaleString()
            : "",
        }));
        const latestNoteEntry = sortedNotes[0] || null;
        const latestNote = latestNoteEntry
          ? {
              note: latestNoteEntry.note,
              createdAt: latestNoteEntry.createdAt
                ? new Date(latestNoteEntry.createdAt).toLocaleString()
                : "",
            }
          : null;

        const statusRaw = application.status || "new";
        const statusDisplay =
          statusLabelMap.get(statusRaw) ||
          statusLabelMap.get(statusRaw.toLowerCase()) ||
          statusRaw.replace(/_/g, " ");

        const intakeEntity =
          application.intake && typeof application.intake === "object"
            ? application.intake
            : null;
        const intakeId =
          intakeEntity?._id ||
          (typeof application.intake === "string" ? application.intake : "");
        const intakeName = application.intakeName || intakeEntity?.name || "";
        const intakeYear = application.intakeYear ?? intakeEntity?.year;
        const intakeLabel =
          intakeName || intakeYear
            ? `${intakeName || "Intake"}${intakeYear ? ` ${intakeYear}` : ""}`
            : "";

        const agentEntity =
          application.agent && typeof application.agent === "object"
            ? application.agent
            : null;
        const agentId =
          agentEntity?._id ||
          (typeof application.agent === "string" ? application.agent : "");
        const agentName = application.agentName || agentEntity?.name || "";
        const agentEmail = application.agentEmail || agentEntity?.email || "";
        const agentLabel =
          agentName || agentEmail
            ? `${agentName || "Agent"}${agentEmail ? ` <${agentEmail}>` : ""}`
            : "";

        return {
          id: application._id,
          program,
          university,
          applicantName: application.applicantName || "N/A",
          applicantEmail,
          applicantEmailDisplay: applicantEmail || "N/A",
          applicantPhone: application.applicantPhone || "",
          statusDisplay,
          statusRaw,
          submittedOn,
          courseId,
          latestNote,
          notes: noteHistory,
          intakeLabel,
          intakeId,
          agentLabel,
          agentId,
          raw: application,
        };
      }),
    [applications]
  );

  const intakeOptions = useMemo(
    () =>
      intakes
        .map((intake) => {
          const id = (intake.id || intake._id || "").toString();
          if (!id) {
            return null;
          }
          const label = `${intake.name || "Intake"}${
            intake.year ? ` ${intake.year}` : ""
          }`.trim();
          return { id, label };
        })
        .filter(Boolean),
    [intakes]
  );

  const agentOptions = useMemo(
    () =>
      agents
        .map((agent) => {
          const id = (agent.id || agent._id || "").toString();
          if (!id) {
            return null;
          }
          const label = `${agent.name || "Agent"}${
            agent.email ? ` <${agent.email}>` : ""
          }`.trim();
          return { id, label };
        })
        .filter(Boolean),
    [agents]
  );

  const filteredIntakeOptions = useMemo(() => {
    if (!intakeFilterQuery.trim()) {
      return intakeOptions;
    }
    const query = intakeFilterQuery.trim().toLowerCase();
    return intakeOptions.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [intakeOptions, intakeFilterQuery]);

  const filteredAgentOptions = useMemo(() => {
    if (!agentFilterQuery.trim()) {
      return agentOptions;
    }
    const query = agentFilterQuery.trim().toLowerCase();
    return agentOptions.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [agentOptions, agentFilterQuery]);

  const currentIntakeFallbackLabel = useMemo(() => {
    if (!editModal.intakeId) {
      return "";
    }
    if (intakeOptions.some((option) => option.id === editModal.intakeId)) {
      return "";
    }
    const source = editModal.application || {};
    const name =
      source.intakeName ||
      source.intakeSnapshot?.name ||
      source.intake?.name ||
      "Current intake";
    const year =
      source.intakeYear ??
      source.intakeSnapshot?.year ??
      source.intake?.year;
    return `${name}${year ? ` ${year}` : ""}`.trim();
  }, [editModal.intakeId, editModal.application, intakeOptions]);

  const currentAgentFallbackLabel = useMemo(() => {
    if (!editModal.agentId) {
      return "";
    }
    if (agentOptions.some((option) => option.id === editModal.agentId)) {
      return "";
    }
    const source = editModal.application || {};
    const name = source.agentName || source.agent?.name || "Current agent";
    const email = source.agentEmail || source.agent?.email || "";
    return `${name}${email ? ` <${email}>` : ""}`.trim();
  }, [editModal.agentId, editModal.application, agentOptions]);

  const selectedIntakeLabel = useMemo(() => {
    if (!intakeFilter) {
      return "";
    }
    const match = intakeOptions.find((option) => option.id === intakeFilter);
    if (!match) {
      return "";
    }
    return match.label;
  }, [intakeFilter, intakeOptions]);

  const selectedAgentLabel = useMemo(() => {
    if (!agentFilter) {
      return "";
    }
    const match = agentOptions.find((option) => option.id === agentFilter);
    if (!match) {
      return "";
    }
    return match.label;
  }, [agentFilter, agentOptions]);
  useEffect(() => {
    if (!intakeFilter) {
      return;
    }
    if (loadingIntakes) {
      return;
    }
    const match = intakeOptions.find((option) => option.id === intakeFilter);
    if (!match) {
      setIntakeFilter("");
      return;
    }
    setIntakeFilterQuery(match.label);
  }, [intakeFilter, intakeOptions, loadingIntakes]);

  useEffect(() => {
    if (!agentFilter) {
      return;
    }
    if (loadingAgents) {
      return;
    }
    const match = agentOptions.find((option) => option.id === agentFilter);
    if (!match) {
      setAgentFilter("");
      return;
    }
    setAgentFilterQuery(match.label);
  }, [agentFilter, agentOptions, loadingAgents]);

  const activeFilterLabels = useMemo(() => {
    const labels = [];
    if (searchTerm) {
      labels.push(`Search: “${searchTerm}”`);
    }
    if (intakeFilter) {
      labels.push(`Intake: ${selectedIntakeLabel || "Selected intake"}`);
    }
    if (agentFilter) {
      labels.push(`Agent: ${selectedAgentLabel || "Selected agent"}`);
    }
    return labels;
  }, [
    searchTerm,
    intakeFilter,
    agentFilter,
    selectedIntakeLabel,
    selectedAgentLabel,
  ]);

  const handleIntakeQueryChange = useCallback(
    (event) => {
      const { value } = event.target;
      setIntakeFilterQuery(value);
      if (intakeFilter) {
        setIntakeFilter("");
      }
    },
    [intakeFilter]
  );

  const handleAgentQueryChange = useCallback(
    (event) => {
      const { value } = event.target;
      setAgentFilterQuery(value);
      if (agentFilter) {
        setAgentFilter("");
      }
    },
    [agentFilter]
  );

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmed = searchInput.trim();
    if (trimmed === searchTerm) {
      return;
    }
    setSearchTerm(trimmed);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    if (searchTerm) {
      setSearchTerm("");
    }
    if (intakeFilter) {
      setIntakeFilter("");
    }
    if (agentFilter) {
      setAgentFilter("");
    }
    setIntakeFilterQuery("");
    setAgentFilterQuery("");
  };

  const openEditModal = (application) => {
    setEditModal({
      show: true,
      application,
      status: application.status || "new",
      note: "",
      intakeId:
        application.intake?._id ||
        application.intakeSnapshot?.id ||
        (typeof application.intake === "string" ? application.intake : ""),
      agentId:
        application.agent?._id ||
        (typeof application.agent === "string" ? application.agent : ""),
      saving: false,
      error: "",
    });
  };

  const closeEditModal = () => {
    setEditModal({
      show: false,
      application: null,
      status: "new",
      note: "",
      intakeId: "",
      agentId: "",
      saving: false,
      error: "",
    });
  };

  const submitEditModal = async (event) => {
    event.preventDefault();
    if (!editModal.application) {
      return;
    }
    setEditModal((prev) => ({ ...prev, saving: true, error: "" }));

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const payload = {
        status: editModal.status,
        intakeId: editModal.intakeId || '',
        agentId: editModal.agentId || ''
      };
      if (typeof editModal.note === "string" && editModal.note.trim()) {
        payload.adminNote = editModal.note.trim();
      }

      const response = await fetch(
        ADMIN_APPLICATIONS_UPDATE_ENDPOINT(editModal.application._id),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to update application.");
      }

      const result = await response.json();
      const updated = result.application;
      setApplications((prev) =>
        prev.map((app) => (app._id === updated._id ? updated : app))
      );
      setNotesModal((prev) => {
        if (
          !prev.show ||
          !editModal.application ||
          editModal.application._id !== updated._id
        ) {
          return prev;
        }
        const noteEntries = Array.isArray(updated.adminNotes)
          ? updated.adminNotes.filter((entry) => entry && entry.note)
          : [];
        if (
          (!noteEntries || noteEntries.length === 0) &&
          typeof updated.adminNote === "string" &&
          updated.adminNote.trim()
        ) {
          noteEntries.push({
            note: updated.adminNote.trim(),
            createdAt: updated.updatedAt || updated.createdAt || null,
          });
        }
        const sortedEntries = [...noteEntries].sort((a, b) => {
          const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bTime - aTime;
        });
        const refreshedNotes = sortedEntries.map((entry) => ({
          note: entry.note,
          createdAt: entry.createdAt
            ? new Date(entry.createdAt).toLocaleString()
            : "",
        }));
        const updatedCourse = updated.course || {};
        const programLabel =
          updated.courseProgramName || updatedCourse.programName || "N/A";
        const applicantLabel = updated.applicantName || "N/A";
        const statusLabel = (updated.status || "new").replace(/_/g, " ");
        const submittedLabel = updated.createdAt
          ? new Date(updated.createdAt).toLocaleString()
          : "";
        const metaParts = [];
        if (submittedLabel) {
          metaParts.push(`Submitted ${submittedLabel}`);
        }
        if (statusLabel) {
          metaParts.push(`Status: ${statusLabel}`);
        }
        return {
          show: true,
          notes: refreshedNotes,
          title: `${programLabel} — ${applicantLabel}`,
          meta: metaParts.join(" • "),
        };
      });
      closeEditModal();
    } catch (err) {
      setEditModal((prev) => ({
        ...prev,
        error: err.message || "Unable to update application.",
      }));
    } finally {
      setEditModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const openProfileModal = async (email) => {
    if (!email) {
      return;
    }

    setProfileModal({
      show: true,
      profile: null,
      loading: true,
      error: "",
      email,
    });

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const params = new URLSearchParams({ email });
      const response = await fetch(
        `${ADMIN_APPLICATION_APPLICANT_ENDPOINT}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to load profile.");
      }

      const payload = await response.json();
      setProfileModal((prev) => ({
        ...prev,
        profile: payload.user || null,
        loading: false,
      }));
    } catch (err) {
      setProfileModal((prev) => ({
        ...prev,
        error: err.message || "Unable to load profile.",
        loading: false,
      }));
    }
  };

  const closeProfileModal = () => {
    setProfileModal({
      show: false,
      profile: null,
      loading: false,
      error: "",
      email: "",
    });
  };

  const openNotesModal = (row) => {
    if (!row) {
      return;
    }
    const title = `${row.program} — ${row.applicantName}`;
    const metaParts = [];
    if (row.submittedOn) {
      metaParts.push(`Submitted ${row.submittedOn}`);
    }
    if (row.statusDisplay) {
      metaParts.push(`Status: ${row.statusDisplay}`);
    }
    setNotesModal({
      show: true,
      notes: Array.isArray(row.notes) ? row.notes : [],
      title,
      meta: metaParts.join(" • "),
    });
  };

  const closeNotesModal = () => {
    setNotesModal({
      show: false,
      notes: [],
      title: "",
      meta: "",
    });
  };

  return (
    <Styles>
      <AdminModalStyles />
      <div className="main-wrapper admin-applications-page">
        <HeaderTwo />
        <BreadcrumbBox title="All Applications" />

        <section className="applications-area">
          <Container>
            <Row>
              <Col md="12">
                {!authLoading && !isAdmin && (
                  <Alert variant="danger">
                    You do not have permission to view this page.
                  </Alert>
                )}
                {isAdmin && (
                  <div className="card-box">
                    <h4>Applications Overview</h4>
                    <div className="filter-bar">
                      <form onSubmit={handleSearchSubmit}>
                        <input
                          type="text"
                          placeholder="Search by applicant, email, or course"
                          value={searchInput}
                          onChange={(event) =>
                            setSearchInput(event.target.value)
                          }
                          disabled={loading}
                        />
                        <button type="submit" disabled={loading}>
                          Search
                        </button>
                        <button
                          type="button"
                          className="clear-btn"
                          onClick={handleClearSearch}
                          disabled={
                            loading ||
                            (!searchTerm &&
                              !searchInput.trim() &&
                              !intakeFilter &&
                              !agentFilter)
                          }
                        >
                          Clear
                        </button>
                      </form>
                      <div className="filter-controls">
                        <div className="control">
                          <input
                            type="text"
                            value={intakeFilterQuery}
                            onChange={handleIntakeQueryChange}
                            placeholder="Filter intakes"
                            disabled={loading || loadingIntakes}
                          />
                          <select
                            value={intakeFilter}
                            onChange={(event) => {
                              const { value } = event.target;
                              setIntakeFilter(value);
                              if (!value) {
                                setIntakeFilterQuery("");
                              } else {
                                const match = intakeOptions.find(
                                  (option) => option.id === value
                                );
                                if (match) {
                                  setIntakeFilterQuery(match.label);
                                }
                              }
                            }}
                            disabled={loading || loadingIntakes}
                          >
                            <option value="">All intakes</option>
                            {filteredIntakeOptions.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {loadingIntakes && (
                            <span className="filter-feedback">
                              Loading intakes…
                            </span>
                          )}
                          {intakeError && (
                            <span className="filter-feedback error">
                              {intakeError}
                            </span>
                          )}
                        </div>
                        <div className="control">
                          <input
                            type="text"
                            value={agentFilterQuery}
                            onChange={handleAgentQueryChange}
                            placeholder="Filter agents"
                            disabled={loading || loadingAgents}
                          />
                          <select
                            value={agentFilter}
                            onChange={(event) => {
                              const { value } = event.target;
                              setAgentFilter(value);
                              if (!value) {
                                setAgentFilterQuery("");
                              } else {
                                const match = agentOptions.find(
                                  (option) => option.id === value
                                );
                                if (match) {
                                  setAgentFilterQuery(match.label);
                                }
                              }
                            }}
                            disabled={loading || loadingAgents}
                          >
                            <option value="">All agents</option>
                            {filteredAgentOptions.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {loadingAgents && (
                            <span className="filter-feedback">
                              Loading agents…
                            </span>
                          )}
                          {agentError && (
                            <span className="filter-feedback error">
                              {agentError}
                            </span>
                          )}
                        </div>
                      </div>
                      {activeFilterLabels.length > 0 && (
                        <div className="active-filter">
                          Active filters: {activeFilterLabels.join(" • ")}
                        </div>
                      )}
                    </div>
                    {error && (
                      <Alert variant="danger" className="mt-3">
                        {error}
                      </Alert>
                    )}
                    {loading ? (
                      <div className="text-center my-4">
                        <Spinner animation="border" role="status" />
                      </div>
                    ) : (
                      <div className="data-grid">
                        {tableRows.length === 0 ? (
                          <Alert variant="info" className="mb-0">
                            No applications found.
                          </Alert>
                        ) : (
                          <>
                            <table>
                              <thead>
                                <tr>
                                  <th>Submitted</th>
                                  <th>Program</th>
                                  <th>University</th>
                                  <th>Applicant Name</th>
                                  <th>Email</th>
                                  <th>Phone</th>
                                  <th>Status</th>
                                  <th>Intake</th>
                                  <th>Agent</th>
                                  <th>Latest Note</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tableRows.map((row) => (
                                  <tr key={row.id}>
                                    <td>{row.submittedOn}</td>
                                    <td>
                                      {row.courseId ? (
                                        <Link
                                          to={`${
                                            process.env.PUBLIC_URL || ""
                                          }/courses/${row.courseId}`}
                                        >
                                          {row.program}
                                        </Link>
                                      ) : (
                                        <span className="muted">
                                          {row.program}
                                        </span>
                                      )}
                                    </td>
                                    <td>{row.university}</td>
                                    <td>
                                      {row.applicantEmail ? (
                                        <button
                                          type="button"
                                          className="link-button"
                                          onClick={() =>
                                            openProfileModal(row.applicantEmail)
                                          }
                                        >
                                          {row.applicantName}
                                        </button>
                                      ) : (
                                        row.applicantName
                                      )}
                                    </td>
                                    <td>{row.applicantEmailDisplay}</td>
                                    <td>
                                      {row.applicantPhone || (
                                        <span className="muted">N/A</span>
                                      )}
                                    </td>
                                    <td className="text-capitalize">
                                      {row.statusDisplay}
                                    </td>
                                    <td>
                                      {row.intakeLabel ? (
                                        row.intakeLabel
                                      ) : (
                                        <span className="muted">N/A</span>
                                      )}
                                    </td>
                                    <td>
                                      {row.agentLabel ? (
                                        row.agentLabel
                                      ) : (
                                        <span className="muted">N/A</span>
                                      )}
                                    </td>
                                    <td>
                                      {row.latestNote ? (
                                        <div className="latest-note">
                                          <span>{row.latestNote.note}</span>
                                          {row.latestNote.createdAt && (
                                            <time>
                                              {row.latestNote.createdAt}
                                            </time>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="muted">N/A</span>
                                      )}
                                    </td>
                                    <td>
                                      <div className="action-buttons">
                                        <button
                                          type="button"
                                          className="icon-button"
                                          onClick={() => openEditModal(row.raw)}
                                          title="Edit application"
                                          aria-label="Edit application"
                                        >
                                          Edit
                                        </button>

                                        <button
                                          type="button"
                                          className="icon-button"
                                          onClick={() => openNotesModal(row)}
                                          title="View notes"
                                          aria-label="View notes"
                                        >
                                          View
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div className="pagination-controls">
                              <div className="page-info">
                                Page {pagination.page} of{" "}
                                {pagination.totalPages} ·{" "}
                                {pagination.totalItems} total
                              </div>
                              <div className="page-actions">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPage((prev) => Math.max(prev - 1, 1))
                                  }
                                  disabled={pagination.page <= 1 || loading}
                                >
                                  Previous
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPage((prev) =>
                                      Math.min(prev + 1, pagination.totalPages)
                                    )
                                  }
                                  disabled={
                                    pagination.page >= pagination.totalPages ||
                                    loading
                                  }
                                >
                                  Next
                                </button>
                                <select
                                  value={pageSize}
                                  onChange={(event) => {
                                    const newSize =
                                      Number(event.target.value) || 10;
                                    setPageSize(newSize);
                                    setPage(1);
                                  }}
                                  disabled={loading}
                                >
                                  {[10, 20, 50, 100].map((size) => (
                                    <option key={size} value={size}>
                                      {size} / page
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </section>

        <FooterTwo />
      </div>

      <Modal
        show={editModal.show}
        onHide={closeEditModal}
        centered
        dialogClassName="admin-applications-modal"
      >
        <Form onSubmit={submitEditModal}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Application</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editModal.error && (
              <Alert variant="danger">{editModal.error}</Alert>
            )}
            {statusTypesError && (
              <Alert variant="warning">{statusTypesError}</Alert>
            )}
            {(() => {
              const app = editModal.application;
              const entries = Array.isArray(app?.adminNotes)
                ? app.adminNotes.filter((entry) => entry && entry.note)
                : [];
              if (
                (!entries || entries.length === 0) &&
                typeof app?.adminNote === "string" &&
                app.adminNote.trim()
              ) {
                entries.push({
                  note: app.adminNote.trim(),
                  createdAt: app.updatedAt || app.createdAt || null,
                });
              }
              if (!entries || entries.length === 0) {
                return null;
              }
              const sortedEntries = [...entries].sort((a, b) => {
                const aTime = a?.createdAt
                  ? new Date(a.createdAt).getTime()
                  : 0;
                const bTime = b?.createdAt
                  ? new Date(b.createdAt).getTime()
                  : 0;
                return bTime - aTime;
              });
            return (
              <div className="note-history">
                <h6>Previous Notes</h6>
                <ul className="list-unstyled">
                  {sortedEntries.map((entry, index) => (
                      <li key={`${entry.createdAt || index}-${index}`}>
                        <div>{entry.note}</div>
                        <small>
                          {entry.createdAt
                            ? new Date(entry.createdAt).toLocaleString()
                            : "Unknown time"}
                        </small>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            <Form.Group controlId="edit_application_status">
              <Form.Label>Status</Form.Label>
              <Form.Control
                as="select"
                value={editModal.status}
                onChange={(event) => {
                  const { value } = event.target;
                  setEditModal((prev) => ({ ...prev, status: value }));
                }}
                disabled={editModal.saving || loadingStatusTypes}
              >
                {allStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Control>
              {loadingStatusTypes && (
                <Form.Text className="text-muted">
                  Loading statuses…
                </Form.Text>
              )}
            </Form.Group>
            <Form.Group controlId="edit_application_intake" className="mt-3">
              <Form.Label>Intake</Form.Label>
              <Form.Control
                as="select"
                value={editModal.intakeId}
                onChange={(event) => {
                  const { value } = event.target;
                  setEditModal((prev) => ({ ...prev, intakeId: value }));
                }}
                disabled={editModal.saving || loadingIntakes}
              >
                <option value="">No intake</option>
                {intakeOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
                {currentIntakeFallbackLabel && (
                  <option value={editModal.intakeId}>
                    {currentIntakeFallbackLabel} (inactive)
                  </option>
                )}
              </Form.Control>
              {loadingIntakes && (
                <Form.Text className="text-muted">Loading intakes…</Form.Text>
              )}
            </Form.Group>
            <Form.Group controlId="edit_application_agent" className="mt-3">
              <Form.Label>Agent (optional)</Form.Label>
              <Form.Control
                as="select"
                value={editModal.agentId}
                onChange={(event) => {
                  const { value } = event.target;
                  setEditModal((prev) => ({ ...prev, agentId: value }));
                }}
                disabled={editModal.saving || loadingAgents}
              >
                <option value="">No agent</option>
                {agentOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
                {currentAgentFallbackLabel && (
                  <option value={editModal.agentId}>
                    {currentAgentFallbackLabel} (inactive)
                  </option>
                )}
              </Form.Control>
              {loadingAgents && (
                <Form.Text className="text-muted">Loading agents…</Form.Text>
              )}
            </Form.Group>
            <Form.Group controlId="edit_application_note" className="mt-3">
              <Form.Label>Admin Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Add any internal notes here"
                value={editModal.note}
                onChange={(event) => {
                  const { value } = event.target;
                  setEditModal((prev) => ({ ...prev, note: value }));
                }}
                disabled={editModal.saving}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={closeEditModal}
              disabled={editModal.saving}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={editModal.saving}>
              {editModal.saving ? "Saving..." : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={notesModal.show}
        onHide={closeNotesModal}
        centered
        dialogClassName="admin-applications-modal notes-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Application Notes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5>{notesModal.title || "Notes"}</h5>
          {notesModal.meta && <p className="meta">{notesModal.meta}</p>}
          {notesModal.notes && notesModal.notes.length > 0 ? (
            <ul className="notes-list">
              {notesModal.notes.map((entry, index) => (
                <li
                  key={`${index}-${
                    entry.createdAt ||
                    (entry.note ? entry.note.slice(0, 10) : "note")
                  }`}
                >
                  <p>{entry.note}</p>
                  {entry.createdAt && <small>{entry.createdAt}</small>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted mb-0">
              No notes recorded for this application yet.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeNotesModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={profileModal.show}
        onHide={closeProfileModal}
        centered
        size="lg"
        dialogClassName="admin-applications-modal profile-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Applicant Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {profileModal.loading && (
            <div className="text-center my-4">
              <Spinner animation="border" role="status" />
            </div>
          )}
          {!profileModal.loading && profileModal.error && (
            <Alert variant="danger">{profileModal.error}</Alert>
          )}
          {!profileModal.loading &&
            !profileModal.error &&
            profileModal.profile && (
              <div className="profile-details">
                <Row className="profile-grid">
                  <Col md="6" className="profile-section">
                    <h5 className="section-heading">Basic Information</h5>
                    <ul className="profile-meta list-unstyled">
                      <li>
                        <strong>Name:</strong> {profileModal.profile.firstName}{" "}
                        {profileModal.profile.lastName}
                      </li>
                      <li>
                        <strong>Email:</strong> {profileModal.profile.email}
                      </li>
                      <li>
                        <strong>Phone:</strong>{" "}
                        {profileModal.profile.phone || "N/A"}
                      </li>
                      <li>
                        <strong>Alternate Email:</strong>{" "}
                        {profileModal.profile.alternateEmail || "N/A"}
                      </li>
                      <li>
                        <strong>Roles:</strong>{" "}
                        {Array.isArray(profileModal.profile.roles) &&
                        profileModal.profile.roles.length > 0
                          ? profileModal.profile.roles.join(", ")
                          : "N/A"}
                      </li>
                    </ul>
                  </Col>
                  <Col md="6" className="profile-section">
                    <h5 className="section-heading">Address</h5>
                    <ul className="profile-meta list-unstyled">
                      <li>
                        <strong>Address 1:</strong>{" "}
                        {profileModal.profile.addressLine1 || "N/A"}
                      </li>
                      <li>
                        <strong>Address 2:</strong>{" "}
                        {profileModal.profile.addressLine2 || "N/A"}
                      </li>
                      <li>
                        <strong>City:</strong>{" "}
                        {profileModal.profile.city || "N/A"}
                      </li>
                      <li>
                        <strong>State:</strong>{" "}
                        {profileModal.profile.state || "N/A"}
                      </li>
                      <li>
                        <strong>Postal Code:</strong>{" "}
                        {profileModal.profile.postalCode || "N/A"}
                      </li>
                      <li>
                        <strong>Country:</strong>{" "}
                        {profileModal.profile.country || "N/A"}
                      </li>
                      <li>
                        <strong>Nationality:</strong>{" "}
                        {profileModal.profile.nationality || "N/A"}
                      </li>
                    </ul>
                  </Col>
                </Row>
                <Row className="profile-grid">
                  <Col md="6" className="profile-section">
                    <h5 className="section-heading">Education</h5>
                    <ul className="profile-meta list-unstyled">
                      <li>
                        <strong>Highest Education:</strong>{" "}
                        {profileModal.profile.highestEducation || "N/A"}
                      </li>
                      <li>
                        <strong>Graduation Year:</strong>{" "}
                        {profileModal.profile.graduationYear || "N/A"}
                      </li>
                      <li>
                        <strong>GPA:</strong>{" "}
                        {profileModal.profile.gpaScore || "N/A"}
                      </li>
                      <li>
                        <strong>IELTS:</strong>{" "}
                        {profileModal.profile.ieltsScore || "N/A"}
                      </li>
                      <li>
                        <strong>TOEFL:</strong>{" "}
                        {profileModal.profile.toeflScore || "N/A"}
                      </li>
                      <li>
                        <strong>PTE:</strong>{" "}
                        {profileModal.profile.pteScore || "N/A"}
                      </li>
                      <li>
                        <strong>Duolingo:</strong>{" "}
                        {profileModal.profile.duolingoScore || "N/A"}
                      </li>
                      <li>
                        <strong>GRE:</strong>{" "}
                        {profileModal.profile.greScore || "N/A"}
                      </li>
                      <li>
                        <strong>GMAT:</strong>{" "}
                        {profileModal.profile.gmatScore || "N/A"}
                      </li>
                      <li>
                        <strong>SAT:</strong>{" "}
                        {profileModal.profile.satScore || "N/A"}
                      </li>
                      <li>
                        <strong>ACT:</strong>{" "}
                        {profileModal.profile.actScore || "N/A"}
                      </li>
                    </ul>
                  </Col>
                  <Col md="6" className="profile-section">
                    <h5 className="section-heading">Preferences & Work</h5>
                    <ul className="profile-meta list-unstyled">
                      <li>
                        <strong>Work Experience (years):</strong>{" "}
                        {profileModal.profile.workExperienceYears || "N/A"}
                      </li>
                      <li>
                        <strong>Current Job Title:</strong>{" "}
                        {profileModal.profile.currentJobTitle || "N/A"}
                      </li>
                      <li>
                        <strong>Current Employer:</strong>{" "}
                        {profileModal.profile.currentEmployer || "N/A"}
                      </li>
                      <li>
                        <strong>Preferred Study Level:</strong>{" "}
                        {profileModal.profile.preferredStudyLevel || "N/A"}
                      </li>
                      <li>
                        <strong>Preferred Country:</strong>{" "}
                        {profileModal.profile.preferredCountry || "N/A"}
                      </li>
                      <li>
                        <strong>Preferred Intake:</strong>{" "}
                        {profileModal.profile.preferredIntake || "N/A"}
                      </li>
                      <li>
                        <strong>Budget Range:</strong>{" "}
                        {profileModal.profile.budgetRange || "N/A"}
                      </li>
                      <li>
                        <strong>Additional Notes:</strong>{" "}
                        {profileModal.profile.additionalNotes || "N/A"}
                      </li>
                    </ul>
                  </Col>
                </Row>
              </div>
            )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeProfileModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Styles>
  );
};

export default AdminApplications;
